import React, { Component } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import SectionLabel from 'components/controls/sectionLabel';
import { DESCRIPTION_ROWS, TITLE_ROWS } from '../../components/autoGrow';
import GrowTextarea from '../../components/growTextarea';
import { GoX } from 'react-icons/go';
import Select from 'components/controls/select';
import StatusPill from '../../components/statusPill';
import {
  workflowAlias, workflowBody, workflowSelection, agentSelection, resolvableAgents, withStepAgent,
  agentLabel, isSameAgent,
} from '../../domain/cellWorkflow';
import DetailsView from './detailsView';
import WorkflowGraph from './workflowGraph';
import AgentPopover, { chipAnchorRect } from '../../components/agentPopover';
import { STATUSES } from '../../domain/schema';
import { upsertMission } from '../../domain/model';
import { sectionsExpanded, toggleSection } from './sectionsExpanded';

// Leading "clear" option for the workspace / cell-workflow name pickers (value '' → save clears to null).
const NONE_OPTION = { value: '', label: '— none —' };

/**
 * MissionPanel — detail panel shown in the right dock when no cell or dependency is selected. It is
 * the resting state of the detail panel; its × collapses the whole detail dock.
 *
 * Layout: a single "Mission" section (`Card.Text as="h6"` + `<hr/>`) with three SectionLabel
 * subsections — Status (the per-cell-status counts), Details (the editable Title + Description), and
 * Workspace (the mission-level default workspace picker). All values read from the store. It mirrors
 * CellPanel's structure and classes (h6 section header, the platform's gray-chip `.properties` /
 * `.prop-name-column` table) so it renders identically with no panel-specific CSS. (Mission switching
 * lives in the widget header, not here — see missions/index.js.)
 *
 * Details, Cell Workflow and Workspace collapse from their own SectionLabel header, the same
 * disclosure CellSections gives the cell panel's three — sectionsExpanded.js is the machinery the two
 * share, so the "which state wins" rule cannot read differently on the two panels the way it has
 * before. A mission carries no draft gate, so unlike a cell's default (open only while it is still
 * being written), a mission's default is simply all three open — there is no state in which any of
 * them is a settled record not worth the screen. A person's own toggle still beats that default until
 * the mission itself changes (`_sectionsKey`), the same "changes underneath them" rule CellSections
 * documents.
 *
 * The mission's Title and Description are editable (missions have no draft gate, so ALWAYS editable).
 * Each field saves on blur via client.setMissionProps — which merges the change into the mission graph
 * object's OWN metadata, non-destructively to its cells — then reflects it locally via
 * onApplyLocal(upsertMission) so the panel updates in place with no reload. The write is skipped when
 * the value is unchanged.
 *
 * The Workspace subsection is the mission's OWN default workspace ({ type, work_folder } | null, stored
 * at graph.metadata.workspace — flattened onto the mission object as mission.workspace by
 * client.listMissions()), which a root cell's workspace_mode "inherit_from_mission" copies (see
 * cellPanel.js). It mirrors CellPanel's specify_workspace picker: a <Select> over
 * client.getTemplates().workspaces (by name), writing the chosen entry's { type, work_folder } as an
 * embedded snapshot — never a reference by name (workspaces are ACTUAL, not templates — schema.js) —
 * via client.setMissionProps(missionId, { workspace }). The current selection is reverse-matched by
 * work_folder since the embedded copy doesn't retain the palette entry's name. Saves immediately on
 * pick (no draft/blur step, like the mission title/description).
 *
 * The Cell Workflow subsection carries its own agent-picking write path (`_saveAgent`), separate from
 * CellWorkflowSection's — the two panels do not share write logic, only the `AgentPopover` control
 * itself and the WorkflowGraph preview underneath it are genuinely shared components. Tapping a
 * `worker_task` step's chip in the preview picks that step (`state.pickedStep` — never ReactFlow node
 * selection, which still marks where the run IS) — including a step with no agent yet, so an agent
 * can be SET, not just changed. The popover, anchored to the tapped chip, appears over
 * `templates.agents`, and choosing a row writes the palette entry's five-field block onto that step
 * (`withStepAgent`) through the same `client.setMissionProps` + `_applyLocal` path `_saveCellWorkflow`
 * already uses. Picking only ever REPLACES a step's agent — there is no "no agent" choice, because
 * `cell.workflow_overrides.agent` (the only other source dispatch would fall back to) has no
 * authoring surface anywhere in the product, so a cleared `worker_task` step would just fail with
 * `agent_unresolved`. A mission's body has no draft gate, so this picker is gated on `canEdit` alone.
 *
 * The popover's other half BUILDS a block instead of picking one, off `templates.harnesses`, and
 * lands in `_buildAgent` — the same write path with a different source for the block.
 *
 * Props:
 *   mission      — mission object { id, title?, description?, workspace? } or null
 *   missionId    — string (the active mission id)
 *   cells        — array of cell vertex objects for the mission
 *   client       — MissionsClient instance
 *   onApplyLocal — (updater: store => store) => void — incremental local store update
 *   onClose      — () => void  — collapse the detail dock
 *   templates    — store.templates ({ workflows, workspaces, agents } palettes)
 */
class MissionPanel extends Component {
  constructor(props) {
    super(props);
    const mission = props.mission || {};
    this.state = {
      titleDraft: mission.title || '',
      descriptionDraft: mission.description || '',
      pickedStep: null,
      anchorRect: null,
      sections: { key: null, expanded: null },
    };
    // Holds the graph preview's DOM node so the popover can be positioned from the tapped chip's own
    // getBoundingClientRect() — see _openAgentPopover.
    this._graphRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    // Re-seed the editable drafts only when the selected mission's identity changes — never on a plain
    // store update (which would clobber an in-progress edit).
    if ((prevProps.mission && prevProps.mission.id) !== (this.props.mission && this.props.mission.id)) {
      this._seedDrafts();
      // A different mission is a different (or absent) step graph — a step picked under the old one
      // would not resolve to anything real in the new one.
      this._clearPickedStep();
    }
  }

  // Opens the popover anchored to the step's own chip. Looked up by DOM query rather than threaded
  // through as a second onSelectStep argument, so WorkflowGraph's own (stepId) => void contract —
  // and every test built against it — stays exactly as it was. Resolving the actual rect is
  // chipAnchorRect's job — see its docstring for why the button itself cannot be measured directly.
  _openAgentPopover(stepId) {
    this.setState({ pickedStep: stepId, anchorRect: chipAnchorRect(this._graphRef.current, stepId) });
  }

  _clearPickedStep() {
    this.setState({ pickedStep: null, anchorRect: null });
  }

  // Which mission the collapse state belongs to — a person's own toggle applies only until this
  // changes underneath them, mirroring CellSections' _key().
  _sectionsKey() {
    return (this.props.mission && this.props.mission.id) || '';
  }

  // All three open — a mission carries no draft gate the way a cell does, so there is no state in
  // which any of Details / Cell Workflow / Workspace is a settled record rather than still current.
  _defaultSections() {
    return { details: true, workflow: true, workspace: true };
  }

  _sections() {
    return sectionsExpanded(this.state.sections, this._sectionsKey(), this._defaultSections());
  }

  _toggleSection(name) {
    this.setState({ sections: toggleSection(this.state.sections, this._sectionsKey(), this._defaultSections(), name) });
  }

  // Seed the editable title/description drafts from the current mission.
  _seedDrafts() {
    const mission = this.props.mission || {};
    this.setState({
      titleDraft: mission.title || '',
      descriptionDraft: mission.description || '',
    });
  }

  // Merge a metadata change into the store locally (no reload) so the switcher + panel reflect it.
  _applyLocal(changed) {
    const { mission, onApplyLocal } = this.props;
    if (!onApplyLocal || !mission) return;
    onApplyLocal(s => upsertMission(s, { ...mission, ...changed }));
  }

  async _saveTitle() {
    const { mission, missionId, client } = this.props;
    if (!mission || !client) return;
    const next = this.state.titleDraft;
    const current = mission.title || '';
    if (next === current) return;
    try {
      await client.setMissionProps(missionId, { title: next });
      this._applyLocal({ title: next });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  async _saveDescription() {
    const { mission, missionId, client } = this.props;
    if (!mission || !client) return;
    const next = this.state.descriptionDraft;
    const current = mission.description || '';
    if (next === current) return;
    try {
      await client.setMissionProps(missionId, { description: next });
      this._applyLocal({ description: next });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  // Workspace-name Select onChange (mission-level default). Copies the chosen palette entry's
  // { type, work_folder } as an embedded snapshot — never a reference by name (see schema.js:
  // workspaces are ACTUAL, not templates) — mirroring CellPanel's _saveWorkspace. Saves immediately
  // (no draft step) since the Select's own onChange already carries the picked value.
  async _saveWorkspace(alias) {
    const { mission, missionId, client, templates } = this.props;
    if (!mission || !client) return;
    const workspaces = (templates && templates.workspaces) || [];
    const currentWorkspace = mission.workspace || null;
    const currentEntry = currentWorkspace
      ? workspaces.find(w => w.work_folder === currentWorkspace.work_folder)
      : null;
    const currentAlias = (currentEntry && currentEntry.alias) || '';
    const selected = alias || '';
    if (selected === currentAlias) return;
    const entry = selected ? workspaces.find(w => w.alias === selected) : null;
    const workspace = entry ? { type: entry.type, work_folder: entry.work_folder } : null;
    try {
      await client.setMissionProps(missionId, { workspace });
      this._applyLocal({ workspace });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  // Cell-workflow Select onChange (mission-level default). Writes the chosen workflow BODY (or null to
  // clear) via client.setMissionProps; a root cell whose cell_workflow_mode is "inherit_from_mission"
  // resolves to this at runtime. Saves immediately on pick (like the title/description/workspace).
  async _saveCellWorkflow(alias) {
    const { mission, missionId, client } = this.props;
    if (!mission || !client) return;
    const current = workflowAlias(mission.cell_workflow);
    const selected = alias || '';
    if (selected === current) return;
    const next = selected || null;
    // A different workflow is a different step graph; a step picked in the old one would not
    // resolve to anything real in the new one.
    this._clearPickedStep();
    try {
      const body = workflowSelection(next, (this.props.templates || {}).workflows || []);
      await client.setMissionProps(missionId, { cell_workflow: body });
      this._applyLocal({ cell_workflow: body });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  // AgentPopover's onPick for the picked step (mission-level default body). Writes the palette
  // entry's five-field block onto that one step and saves the same way _saveCellWorkflow does.
  //
  // Two guards, in order: re-picking the step's own current agent is a no-op (mirrors every sibling
  // save in this file), and an alias agentSelection cannot resolve (a malformed or stale palette
  // entry) refuses the write rather than falling through into anything else.
  async _saveAgent(alias) {
    const { mission, missionId, client, templates } = this.props;
    const { pickedStep } = this.state;
    if (!mission || !client || !pickedStep) return;
    const body = workflowBody(mission.cell_workflow, (templates || {}).workflows || []);
    if (!body) return;
    const pickedNode = Array.isArray(body.nodes) ? body.nodes.find(n => n && n.id === pickedStep) : null;
    const current = (pickedNode && agentLabel(pickedNode)) || '';
    if (alias === current) return;
    const agents = (templates && templates.agents) || [];
    const agent = agentSelection(alias, agents);
    if (!agent) return;
    const nextBody = withStepAgent(body, pickedStep, agent);
    try {
      await client.setMissionProps(missionId, { cell_workflow: nextBody });
      this._applyLocal({ cell_workflow: nextBody });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  // AgentPopover's onBuild for the picked step: the block came off the four sliders rather than the
  // palette, and everything after that is _saveAgent's path exactly. Mirrors
  // cellWorkflowSection._buildAgent — the two panels share the control, not the write.
  //
  // Two guards, differing from _saveAgent's for the same two reasons: there is no alias to compare
  // against, so the no-op guard compares the WHOLE block (a release that changed nothing must not
  // post a save); and there is no null resolution to absorb, because customAgentBlock already
  // returned either null or a complete block — so the guard is simply that a block arrived.
  async _buildAgent(block) {
    const { mission, missionId, client, templates } = this.props;
    const { pickedStep } = this.state;
    if (!mission || !client || !pickedStep || !block) return;
    const body = workflowBody(mission.cell_workflow, (templates || {}).workflows || []);
    if (!body) return;
    const pickedNode = Array.isArray(body.nodes) ? body.nodes.find(n => n && n.id === pickedStep) : null;
    if (pickedNode && isSameAgent(pickedNode.agent, block)) return;
    const nextBody = withStepAgent(body, pickedStep, block);
    try {
      await client.setMissionProps(missionId, { cell_workflow: nextBody });
      this._applyLocal({ cell_workflow: nextBody });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  render() {
    const { mission, missionId, cells, onClose, templates } = this.props;
    // A mission has no draft gate of its own — the only thing that makes this panel read-only is a
    // committed revision, a closed session or no write access. Default true so an embedding that
    // does not express the seam behaves as it did.
    const canEdit = this.props.canEdit !== false;
    const { titleDraft, descriptionDraft } = this.state;

    // Mission-level default workspace picker (mirrors CellPanel's specify_workspace picker). The
    // current selection is reverse-matched by work_folder since the embedded copy on the mission
    // doesn't retain the palette entry's alias.
    const missionWorkspace = (mission && mission.workspace) || null;
    const workspaceTemplates = (templates && templates.workspaces) || [];
    const workspaceOptions = [
      NONE_OPTION,
      ...(Array.isArray(workspaceTemplates)
        ? workspaceTemplates.map(w => w.alias).filter(Boolean).map(a => ({ value: a, label: a }))
        : []),
    ];
    const selectedWorkspaceEntry = missionWorkspace
      ? workspaceTemplates.find(w => w.work_folder === missionWorkspace.work_folder) || null
      : null;
    const workspaceSelectValue = (selectedWorkspaceEntry
      && workspaceOptions.find(o => o.value === selectedWorkspaceEntry.alias)) || NONE_OPTION;

    // Mission-level default cell workflow (workflows is a LIST of { alias, ... }); root cells whose
    // cell_workflow_mode is "inherit_from_mission" resolve to this.
    const missionCellWorkflow = workflowAlias(mission && mission.cell_workflow);
    const workflowTemplates = (templates && templates.workflows) || [];
    const workflowOptions = [
      NONE_OPTION,
      ...(Array.isArray(workflowTemplates)
        ? workflowTemplates.map(w => w.alias).filter(Boolean).map(a => ({ value: a, label: a }))
        : []),
    ];
    const workflowSelectValue = workflowOptions.find(o => o.value === missionCellWorkflow) || NONE_OPTION;
    // The mission's OWN embedded body — the palette is consulted only to resolve a legacy bare alias,
    // exactly as the cell section does. Previewing the palette entry instead would show a graph the
    // mission does not have the moment somebody edits the library.
    const selectedWorkflow = workflowBody(mission && mission.cell_workflow, workflowTemplates);

    // The step picked in the preview, and the agent picker built from it — mirrors cellWorkflowSection.
    const { pickedStep, anchorRect } = this.state;
    const pickedNode = (pickedStep && selectedWorkflow && Array.isArray(selectedWorkflow.nodes))
      ? selectedWorkflow.nodes.find(n => n && n.id === pickedStep) : null;
    // Only entries agentSelection can actually resolve — never offer a choice that would silently
    // write nothing (see _saveAgent's guard, and resolvableAgents in domain/cellWorkflow.js).
    const pickableAgents = resolvableAgents((templates && templates.agents) || []);
    // '' for no agent at all, 'custom' for an alias-less block (still HAS an agent — see
    // agentLabel's docstring), or the block's own alias. The popover checks a row only when this
    // matches ITS alias, so 'custom' — not one of the listed choices — correctly checks nothing.
    const pickedAgentAlias = (pickedNode && agentLabel(pickedNode)) || '';

    // Status — per-cell-status counts, in lifecycle order, omitting empty buckets.
    const counts = {};
    (cells || []).forEach(c => {
      const status = (c.props && c.props.status) || 'draft';
      counts[status] = (counts[status] || 0) + 1;
    });
    const presentStatuses = STATUSES.filter(s => counts[s]);

    // Details / Cell Workflow / Workspace each collapse from their own SectionLabel header, all
    // three open on arrival — see _defaultSections.
    const expandedSections = this._sections();
    const section = (name) => (
      { collapsible: true, expanded: expandedSections[name], onToggle: () => this._toggleSection(name) }
    );

    return (
      <>
        {onClose && (
          <span className="workflow-controls-pane-right">
            <Button variant="light" onClick={onClose} aria-label="Close panel"><GoX /></Button>
          </span>
        )}

        {/* Mission — one section with Status / Details / Workspace subsections, mirroring the
            Playground panel's layout: an h6 header + SectionLabel sub-labels with gray-chip tables. */}
        <Card.Text as="h6">Mission</Card.Text>
        <hr />

        {/* Mission ID + per-cell-status counts — one gray-chip properties table (value cells carry the
            .prop-value-column background). No separate Status subsection. */}
        <Card.Text>
          <table className="properties">
            <tbody>
              <tr>
                <td className="prop-name-column">Mission ID</td>
                <td className="prop-value-column"><code>{mission ? mission.id : ''}</code></td>
              </tr>
              {/* The mission's OWN status, mirroring the cell panel's ID-then-Status pair. It is not
                  cosmetic: the orchestrator never re-reads an `archived` mission, so a cell added to
                  one is never picked up and nothing else on this screen says why. */}
              <tr>
                <td className="prop-name-column">Status</td>
                <td className="prop-value-column">
                  {mission ? <StatusPill status={mission.status} /> : null}
                </td>
              </tr>
              {presentStatuses.map(s => (
                <tr key={s}>
                  <td className="prop-name-column"><StatusPill status={s} /></td>
                  <td className="prop-value-column">{counts[s]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card.Text>

        {/* Details — editable Title + Description (a mission has no draft gate, so the only thing
            that makes these read-only is the revision or the session; saved on blur via
            client.setMissionProps, then reflected locally via onApplyLocal(upsertMission)).
            TITLE and DESCRIPTION are what these fields are called on every surface that touches them —
            this panel, the cell panel, both creation modals and the orchestrator. */}
        <SectionLabel {...section('details')}>Details</SectionLabel>
        {expandedSections.details && (!canEdit ? (
          // The same two lines the cell panel falls back to, through the same view — a mission's
          // title and description are the same pair of fields, and two renderings of them is how
          // the two panels come to disagree about what read-only prose looks like.
          <DetailsView title={(mission && mission.title) || missionId || ''}
            description={(mission && mission.description) || ''} />
        ) : (
        <>
        <Form.Group>
          <GrowTextarea
            rows={1}
            maxRows={TITLE_ROWS}
            value={titleDraft}
            placeholder="Mission title…"
            disabled={!mission}
            onChange={value => this.setState({ titleDraft: value })}
            onBlur={() => this._saveTitle()}
          />
          <Form.Text muted>What this mission is called.</Form.Text>
        </Form.Group>
        <Form.Group>
          <GrowTextarea
            rows={2}
            maxRows={DESCRIPTION_ROWS}
            value={descriptionDraft}
            placeholder="Mission description…"
            disabled={!mission}
            onChange={value => this.setState({ descriptionDraft: value })}
            onBlur={() => this._saveDescription()}
          />
          <Form.Text muted>What this mission is about.</Form.Text>
        </Form.Group>
        </>
        ))}
        <SectionLabel {...section('workflow')}>Cell Workflow</SectionLabel>
        {expandedSections.workflow && (canEdit ? (
          <>
            <Form.Group>
              <Select
                options={workflowOptions}
                value={workflowSelectValue}
                onChange={opt => this._saveCellWorkflow(opt ? opt.value : '')}
                menuPortalTarget={document.body}
                placeholder="— none —"
                isDisabled={!mission}
              />
              {/* Preview the chosen workflow's step graph right under the picker. Tapping a step's
                  chip opens the agent popover anchored to it — see _openAgentPopover. */}
              {selectedWorkflow && (
                <div ref={this._graphRef}>
                  <WorkflowGraph workflow={selectedWorkflow}
                    onSelectStep={stepId => this._openAgentPopover(stepId)} />
                </div>
              )}
              <Form.Text muted>Default steps for this mission's cells.</Form.Text>
            </Form.Group>
            {pickedStep && pickedNode && (
              <AgentPopover
                anchorRect={anchorRect}
                stepId={pickedStep}
                agents={pickableAgents}
                selectedAlias={pickedAgentAlias}
                // _saveAgent reads state.pickedStep synchronously before it awaits anything, so it
                // must run BEFORE _clearPickedStep's setState — reversing the order would clear the
                // very step id the write needs. A picked row writes, then closes.
                onPick={alias => { this._saveAgent(alias); this._clearPickedStep(); }}
                harnesses={(templates && templates.harnesses) || []}
                currentAgent={pickedNode && pickedNode.agent}
                // Deliberately NOT paired with _clearPickedStep, unlike onPick above. A pick is a
                // decision and closing on it is the answer; a slider is a tuning gesture, and
                // closing would mean re-opening the popover to make the next adjustment.
                onBuild={block => this._buildAgent(block)}
                onClose={() => this._clearPickedStep()}
              />
            )}
          </>
        ) : (
          // No picker, so no help text about choosing: what is left to say is what it IS. The name
          // and its step graph are exactly what the cell panel shows for a frozen workflow.
          <>
            {selectedWorkflow ? (
              <>
                <table className="properties">
                  <tbody>
                    <tr>
                      <td className="prop-name-column">Workflow</td>
                      <td className="prop-value-column"><code>{selectedWorkflow.alias}</code></td>
                    </tr>
                  </tbody>
                </table>
                <WorkflowGraph workflow={selectedWorkflow} />
              </>
            ) : <p className="mb-0">None</p>}
          </>
        ))}
        <SectionLabel {...section('workspace')}>Workspace</SectionLabel>
        {expandedSections.workspace && (canEdit ? (
          <Form.Group>
            <Select
              options={workspaceOptions}
              value={workspaceSelectValue}
              onChange={opt => this._saveWorkspace(opt ? opt.value : '')}
              menuPortalTarget={document.body}
              placeholder="— none —"
              isDisabled={!mission}
            />
            <Form.Text muted>Default place its agents work.</Form.Text>
          </Form.Group>
        ) : (
          // Off the ENTRY, not off the select's value: that one falls back to NONE_OPTION rather
          // than to null, so a mission with no workspace would have read "— none —" — a picker's
          // empty-choice placeholder presented as a fact.
          <p className="mb-0">{selectedWorkspaceEntry ? selectedWorkspaceEntry.alias : 'None'}</p>
        ))}
      </>
    );
  }
}

export default MissionPanel;
